/**
 * Shared helpers used by every Accounting sub-report.
 *
 * Extracted from the old 2,188-line `Accounting.js` (iter 60d refactor).
 * Keeping these central means each report file stays under ~200 lines and
 * has one job.
 */
import api from "@/lib/api";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, CheckCircle2, AlertCircle, Download } from "lucide-react";

export const toDate = (d) => (d instanceof Date ? d : new Date(d));
export const fmtISO = (d) => format(toDate(d), "yyyy-MM-dd");

// iter 72i — Rail section header used at the top of each per-currency
// block in multi-rail reports (Balance Sheet, P&L). Makes it obvious where
// the "USD section" ends and the "NGN section" begins when scanning.
const RAIL_META = {
  USD: { color: "#1E5A96", tint: "#EAF3FB", label: "US Dollar" },
  NGN: { color: "#2F7A55", tint: "#EAF5EF", label: "Nigerian Naira" },
};
export function RailSectionHeader({ currency, subtitle, right, testId }) {
  const meta = RAIL_META[currency] || { color: "#2F4F4F", tint: "#F0ECE1", label: currency };
  return (
    <div
      data-testid={testId || `rail-section-header-${currency}`}
      className="rounded-lg border-l-4 flex items-center justify-between gap-3 px-4 py-3 mt-2"
      style={{ borderLeftColor: meta.color, background: meta.tint }}
    >
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: meta.color }}>
          {currency} section
        </div>
        <div className="text-sm font-medium text-[var(--text)]">
          {meta.label}{subtitle ? <span className="text-[var(--muted-text)] font-normal"> · {subtitle}</span> : null}
        </div>
      </div>
      {right}
    </div>
  );
}

export async function downloadStream(path, params, filename) {
  const res = await api.get(path, { params, responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function DateField({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{label}</div>
      <Popover>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testId} className="w-full justify-start font-normal h-9">
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {value ? format(toDate(value), "MMM d, yyyy") : "Pick a date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar mode="single" selected={value ? toDate(value) : undefined} onSelect={(d) => d && onChange(d)} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function BalancedBadge({ balanced, difference, currency }) {
  return balanced ? (
    <span data-testid="balanced-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF4EE] text-[#2F6B4A] px-3 py-1 text-xs">
      <CheckCircle2 className="w-3.5 h-3.5" /> Books balance
    </span>
  ) : (
    <span data-testid="unbalanced-badge" className="inline-flex items-center gap-1.5 rounded-full bg-[#FCE7E9] text-[#B4283A] px-3 py-1 text-xs">
      <AlertCircle className="w-3.5 h-3.5" /> Out by {formatCurrency(Math.abs(difference || 0), currency)}
    </span>
  );
}

export function CsvButton({ onClick, testId }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-testid={testId}
      onClick={onClick}
      className="h-9 gap-2"
    >
      <Download className="w-3.5 h-3.5" /> CSV
    </Button>
  );
}

export function DeltaBadge({ delta, pct, currency, inverse = false }) {
  if (delta === 0 || delta == null) {
    return <span className="text-xs text-[var(--muted-text)] font-mono-data">—</span>;
  }
  const good = inverse ? delta < 0 : delta > 0;
  return (
    <span className={`text-xs font-mono-data ${good ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
      {delta > 0 ? "+" : ""}{formatCurrency(delta, currency)}
      {pct != null && ` (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`}
    </span>
  );
}
